import React, { useEffect, useState } from "react";
import axiosInstance from "src/api/axios";
import { toast } from "react-toastify";
import classNames from "classnames";
import Chats from "src/components/Chats";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  Trash2,
  RefreshCw,
  MessageSquare,
  CornerDownRight,
  EyeOff,
  Eye,
  ExternalLink,
} from "lucide-react";
import {
  getInstagramComments,
  deleteInstagramComment,
  syncInstagramComments,
  createMentionComment,
  replyToComment,
  setCommentHidden,
} from "src/api/instagramBusiness";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface InstagramBusinessUser {
  id: string;
  username: string;
  name?: string;
  email?: string;
  profilePicture?: string;
  [key: string]: any;
}

const InstagramBusinessDashboard: React.FC = () => {
  const [user, setUser] = useState<InstagramBusinessUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [media, setMedia] = useState<any[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [mentions, setMentions] = useState<any[]>([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [mentionsError, setMentionsError] = useState<string | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null,
  );
  const [syncingComments, setSyncingComments] = useState(false);
  // Commenting on a mention/tagged media (Mentions tab).
  const [mentionCommentFor, setMentionCommentFor] = useState<string | null>(
    null,
  );
  const [mentionCommentText, setMentionCommentText] = useState("");
  const [postingMention, setPostingMention] = useState(false);
  // Replying to a specific comment (Comments tab).
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [postingReply, setPostingReply] = useState(false);
  const [hidingCommentId, setHidingCommentId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    "media" | "mentions" | "chat" | "comments"
  >("comments");
  const [insights, setInsights] = useState<any[]>([]);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError(null);
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/user`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined,
        );
        const userData =
          response.data && response.data.data && response.data.data[0];
        if (userData) {
          setUser(userData);
        } else {
          setUser(null);
          setError("No user data found.");
        }
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch user data",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchMedia = async () => {
      setMediaLoading(true);
      setMediaError(null);
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/media`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined,
        );
        setMedia(response.data?.data || []);
      } catch (err: any) {
        setMediaError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch media",
        );
      } finally {
        setMediaLoading(false);
      }
    };
    fetchMedia();
  }, []);

  useEffect(() => {
    const fetchMentions = async () => {
      setMentionsLoading(true);
      setMentionsError(null);
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/mentions`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined,
        );
        setMentions(response.data?.data || []);
      } catch (err: any) {
        setMentionsError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch mentions",
        );
      } finally {
        setMentionsLoading(false);
      }
    };
    fetchMentions();
  }, []);

  const fetchComments = async () => {
    setCommentsLoading(true);
    setCommentsError(null);
    try {
      const response = await getInstagramComments();
      setComments(response?.data || []);
    } catch (err: any) {
      setCommentsError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch comments",
      );
    } finally {
      setCommentsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, []);

  const handleDeleteComment = async (comment: any) => {
    const id = comment._id || comment.comment_id;
    if (!id) return;
    const hasReplies =
      !comment.parent_comment_id &&
      comments.some(
        (c) => c.parent_comment_id === comment.comment_id && c.status !== "deleted",
      );
    const confirmMsg = hasReplies
      ? "Delete this comment on Instagram? Its replies will be deleted too. This cannot be undone."
      : "Delete this comment on Instagram? This cannot be undone.";
    if (!window.confirm(confirmMsg)) return;
    setDeletingCommentId(id);
    try {
      await deleteInstagramComment(id);
      // Gray out locally by flipping status to "deleted". Deleting a top-level
      // comment cascades to its replies on Instagram, so grey those too.
      setComments((prev) =>
        prev.map((c) => {
          const isTarget = (c._id || c.comment_id) === id;
          const isReplyOfTarget =
            !comment.parent_comment_id &&
            c.parent_comment_id &&
            c.parent_comment_id === comment.comment_id;
          return isTarget || isReplyOfTarget
            ? { ...c, status: "deleted", deletedAt: new Date().toISOString() }
            : c;
        }),
      );
      toast.success("Comment deleted");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to delete comment",
      );
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleSyncComments = async () => {
    setSyncingComments(true);
    setCommentsError(null);
    try {
      const response = await syncInstagramComments();
      setComments(response?.data || []);
      const removed = response?.deletedCount || 0;
      toast.success(
        removed > 0
          ? `Synced — ${removed} comment(s) deleted on Instagram`
          : "Comments up to date"
      );
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to sync comments"
      );
    } finally {
      setSyncingComments(false);
    }
  };

  // Post a comment on a mention/tagged media; the created comment is stored
  // server-side and surfaced in the Comments tab.
  const handleCreateMentionComment = async (mediaId: string) => {
    const text = mentionCommentText.trim();
    if (!text) return;
    setPostingMention(true);
    try {
      const response = await createMentionComment(mediaId, text);
      if (response?.data) {
        setComments((prev) => [response.data, ...prev]);
      }
      setMentionCommentText("");
      setMentionCommentFor(null);
      toast.success("Comment posted — see the Comments tab");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to post comment",
      );
    } finally {
      setPostingMention(false);
    }
  };

  // Reply to a specific comment; the reply is threaded under its parent.
  const handleReplyToComment = async (parent: any) => {
    const id = parent._id || parent.comment_id;
    const text = replyText.trim();
    if (!id || !text) return;
    setPostingReply(true);
    try {
      const response = await replyToComment(id, text);
      if (response?.data) {
        setComments((prev) => [...prev, response.data]);
      }
      setReplyText("");
      setReplyFor(null);
      toast.success("Reply posted");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to post reply",
      );
    } finally {
      setPostingReply(false);
    }
  };

  // Hide or unhide a comment on Instagram (own-media comments only).
  const handleToggleHidden = async (comment: any) => {
    const id = comment._id || comment.comment_id;
    if (!id) return;
    const hide = !comment.hidden;
    setHidingCommentId(id);
    try {
      await setCommentHidden(id, hide);
      setComments((prev) =>
        prev.map((c) =>
          (c._id || c.comment_id) === id ? { ...c, hidden: hide } : c,
        ),
      );
      toast.success(hide ? "Comment hidden" : "Comment unhidden");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to update comment",
      );
    } finally {
      setHidingCommentId(null);
    }
  };

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/insights`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined,
        );
        console.log("insights", response.data?.data || []);
        setInsights(response.data?.data || []);
      } catch (err: any) {
        console.log(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch mentions",
        );
      }
    };
    fetchInsights();
  }, []);

  return (
    <div
      className={classNames(
        "flex flex-col items-center min-h-screen bg-gray-50",
        {
          "justify-center": !user,
        },
      )}
    >
      <h1 className="sr-only">Instagram Business Dashboard</h1>
      {loading ? (
        <p className="text-lg text-gray-700">Loading user data...</p>
      ) : error ? (
        <div className="text-lg text-red-500 flex flex-col items-center">
          {/* <p>{error}</p> */}
          <p className="mt-4 text-gray-700">
            Connect your Instagram account to use this dashboard.{" "}
            <a
              href="/instagram-connect"
              className="text-blue-600 underline hover:text-blue-800"
            >
              Click here
            </a>
            .
          </p>
        </div>
      ) : user ? (
        <>
          <div className="bg-white shadow-md rounded-xl p-8 w-full max-w-xl flex flex-col">
            <div className="flex items-center mb-4">
              {user.profile_picture_url && (
                <img
                  src={user.profile_picture_url}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover bg-gray-200 mr-6"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-2xl font-bold text-gray-900 leading-tight">
                      {user.name}
                    </div>
                    <a
                      href={`https://instagram.com/${user.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg text-gray-500 hover:underline block"
                    >
                      @{user.username}
                    </a>
                  </div>
                  <div className="flex gap-8 mt-4 sm:mt-0 sm:ml-8">
                    <div className="text-center">
                      <div className="font-bold text-gray-900 text-lg">
                        Followers
                      </div>
                      <div className="text-gray-800 text-lg">
                        {user.followers_count}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-gray-900 text-lg">
                        Following
                      </div>
                      <div className="text-gray-800 text-lg">
                        {user.follows_count}
                      </div>
                    </div>
                    <div className="text-center">
                      {/* <div className="font-bold text-gray-900 text-lg">
                        <Tooltip.Provider>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <span>Reach</span>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                sideOffset={5}
                                className="bg-black text-white px-2 py-1 rounded text-xs"
                              >
                                Reach (Last 7 days)
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </Tooltip.Provider>
                      </div> */}
                      {/* <div className="text-gray-800 text-lg">
                        {!insights?.at(0)?.values?.at(0)?.value &&
                        !insights?.at(0)?.values?.at(1)?.value ? (
                          <>
                            {insights?.at(0)?.values?.at(0)?.value ?? 0}-
                            {insights?.at(0)?.values?.at(1)?.value ?? 0}
                          </>
                        ) : (
                          0
                        )}
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {user.biography && (
              <div className="mt-2 text-gray-700 text-lg whitespace-pre-line">
                {user.biography}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="mt-10 w-full max-w-7xl">
            <div className="flex border-b mb-4">
              {/* <button
                className={classNames(
                  "px-4 py-2 font-semibold",
                  activeTab === "media"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-blue-600"
                )}
                onClick={() => setActiveTab("media")}
              >
                Media
              </button> */}
              <button
                className={classNames(
                  "px-4 py-2 font-semibold",
                  activeTab === "comments"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-blue-600",
                )}
                onClick={() => setActiveTab("comments")}
              >
                Comments
              </button>
              <button
                className={classNames(
                  "px-4 py-2 font-semibold",
                  activeTab === "mentions"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-blue-600",
                )}
                onClick={() => setActiveTab("mentions")}
              >
                Mentions
              </button>
              <button
                className={classNames(
                  "px-4 py-2 font-semibold",
                  activeTab === "chat"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-blue-600",
                )}
                onClick={() => setActiveTab("chat")}
              >
                Chat
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "media" && (
              <div>
                <h2 className="text-xl font-bold mb-2">Media</h2>
                {mediaLoading ? (
                  <p>Loading media...</p>
                ) : mediaError ? (
                  <p className="text-red-500">{mediaError}</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg shadow">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left">Preview</th>
                          <th className="px-4 py-2 text-left">Type</th>
                          <th className="px-4 py-2 text-left">Caption</th>
                          <th className="px-4 py-2 text-left">Timestamp</th>
                          <th className="px-4 py-2 text-left">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {media.map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="px-4 py-2">
                              {item.media_type === "IMAGE" ||
                              item.media_type === "CAROUSEL_ALBUM" ? (
                                <img
                                  src={item.media_url}
                                  alt="media"
                                  className="w-16 h-16 object-cover rounded"
                                />
                              ) : item.media_type === "VIDEO" ? (
                                <video
                                  src={item.media_url}
                                  controls
                                  className="w-16 h-16 object-cover rounded"
                                />
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td className="px-4 py-2">{item.media_type}</td>
                            <td
                              className="px-4 py-2 max-w-xs truncate"
                              title={item.caption}
                            >
                              {item.caption || "-"}
                            </td>
                            <td className="px-4 py-2">
                              {item.timestamp
                                ? new Date(item.timestamp).toLocaleString()
                                : "-"}
                            </td>
                            <td className="px-4 py-2">
                              <a
                                href={item.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                View
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {media.length === 0 && (
                      <div className="p-4 text-gray-500">No media found.</div>
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === "mentions" && (
              <div>
                <h2 className="text-xl font-bold mb-2">Mentions</h2>
                {mentionsLoading ? (
                  <p>Loading mentions...</p>
                ) : mentionsError ? (
                  <p className="text-red-500">{mentionsError}</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg shadow">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left">Preview</th>
                          <th className="px-4 py-2 text-left">Type</th>
                          <th className="px-4 py-2 text-left">Caption</th>
                          <th className="px-4 py-2 text-left">Timestamp</th>
                          <th className="px-4 py-2 text-left">Link</th>
                          <th className="px-4 py-2 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mentions.map((item) => (
                          <React.Fragment key={item.id}>
                            <tr className="border-t">
                              <td className="px-4 py-2">
                                {item.media_type === "IMAGE" ||
                                item.media_type === "CAROUSEL_ALBUM" ? (
                                  <img
                                    src={item.media_url}
                                    alt="media"
                                    className="w-16 h-16 object-cover rounded"
                                  />
                                ) : item.media_type === "VIDEO" ? (
                                  <video
                                    src={item.media_url}
                                    controls
                                    className="w-16 h-16 object-cover rounded"
                                  />
                                ) : (
                                  <span>-</span>
                                )}
                              </td>
                              <td className="px-4 py-2">{item.media_type}</td>
                              <td
                                className="px-4 py-2 max-w-xs truncate"
                                title={item.caption}
                              >
                                {item.caption || "-"}
                              </td>
                              <td className="px-4 py-2">
                                {item.timestamp
                                  ? new Date(item.timestamp).toLocaleString()
                                  : "-"}
                              </td>
                              <td className="px-4 py-2">
                                <a
                                  href={item.permalink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  View
                                </a>
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  onClick={() =>
                                    setMentionCommentFor(
                                      mentionCommentFor === item.id
                                        ? null
                                        : item.id,
                                    )
                                  }
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                >
                                  <MessageSquare size={16} />
                                  Comment
                                </button>
                              </td>
                            </tr>
                            {mentionCommentFor === item.id && (
                              <tr className="border-t bg-gray-50">
                                <td className="px-4 py-2" colSpan={6}>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      autoFocus
                                      value={mentionCommentText}
                                      onChange={(e) =>
                                        setMentionCommentText(e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                          handleCreateMentionComment(item.id);
                                        if (e.key === "Escape")
                                          setMentionCommentFor(null);
                                      }}
                                      placeholder="Write a comment..."
                                      className="flex-1 border rounded px-3 py-1 text-sm"
                                    />
                                    <button
                                      onClick={() =>
                                        handleCreateMentionComment(item.id)
                                      }
                                      disabled={
                                        postingMention ||
                                        !mentionCommentText.trim()
                                      }
                                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                                    >
                                      {postingMention ? "Posting..." : "Post"}
                                    </button>
                                    <button
                                      onClick={() => setMentionCommentFor(null)}
                                      className="px-2 py-1 text-sm text-gray-600 hover:text-gray-800"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                    {mentions.length === 0 && (
                      <div className="p-4 text-gray-500">
                        No mentions found.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === "chat" && (
              <div>
                <h2 className="text-xl font-bold mb-2">Chat / Messaging</h2>
                <Chats igUsername={user.username} />
              </div>
            )}
            {activeTab === "comments" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-bold">Comments</h2>
                  <button
                    onClick={handleSyncComments}
                    disabled={syncingComments}
                    title="Sync with Instagram (detect comments deleted on IG)"
                    aria-label="Sync comments"
                    className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-blue-600 disabled:opacity-50"
                  >
                    <RefreshCw
                      size={16}
                      className={syncingComments ? "animate-spin" : ""}
                    />
                    {syncingComments ? "Syncing..." : "Refresh"}
                  </button>
                </div>
                {commentsLoading ? (
                  <p>Loading comments...</p>
                ) : commentsError ? (
                  <p className="text-red-500">{commentsError}</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg shadow">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left">Who Posted</th>
                          <th className="px-4 py-2 text-left">Post</th>
                          <th className="px-4 py-2 text-left">Comment</th>
                          <th className="px-4 py-2 text-left">Timing</th>
                          <th className="px-4 py-2 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Thread comments: top-level rows, each followed by its replies.
                          const repliesByParent: Record<string, any[]> = {};
                          comments
                            .filter((c) => c.parent_comment_id)
                            .forEach((c) => {
                              (repliesByParent[c.parent_comment_id] ||= []).push(
                                c,
                              );
                            });
                          const topLevel = comments.filter(
                            (c) => !c.parent_comment_id,
                          );

                          const renderRow = (item: any, isReply: boolean) => {
                            const id = item._id || item.comment_id;
                            const isDeleted = item.status === "deleted";
                            const isOutgoing = item.direction === "outgoing";
                            // Incoming comments are on our media (deletable unless
                            // flagged false). Comments we authored are deletable
                            // only when explicitly flagged (a /replies reply on our
                            // own media); all other outgoing live on others' media.
                            const canDelete = isOutgoing
                              ? item.deletable === true
                              : item.deletable !== false;
                            // Hiding only applies to others' comments on our own
                            // media: our own comments are always shown by IG, and
                            // live-video comments aren't supported.
                            const canHide =
                              canDelete &&
                              !isOutgoing &&
                              (item.media_product_type || "").toUpperCase() !==
                                "LIVE";
                            return (
                              <tr
                                key={id}
                                className={classNames("border-t", {
                                  "opacity-50 bg-gray-100": isDeleted,
                                  "bg-blue-50": isReply && !isDeleted,
                                })}
                              >
                                <td className="px-4 py-2">
                                  <div
                                    className={classNames(
                                      "flex items-center gap-1",
                                      { "pl-6": isReply },
                                    )}
                                  >
                                    {isReply && (
                                      <CornerDownRight
                                        size={14}
                                        className="text-gray-400 shrink-0"
                                      />
                                    )}
                                    {isOutgoing ? (
                                      <span className="font-medium text-gray-800">
                                        You
                                        {item.from_username
                                          ? ` (@${item.from_username})`
                                          : ""}
                                      </span>
                                    ) : item.from_username ? (
                                      <a
                                        href={`https://instagram.com/${item.from_username}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline"
                                      >
                                        @{item.from_username}
                                      </a>
                                    ) : (
                                      item.from_id || "-"
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-2">
                                    {item.media_thumbnail ? (
                                      <a
                                        href={
                                          item.media_permalink ||
                                          item.media_thumbnail
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="shrink-0"
                                      >
                                        <img
                                          src={item.media_thumbnail}
                                          alt="media"
                                          loading="lazy"
                                          className="w-12 h-12 object-cover rounded border border-gray-200"
                                        />
                                      </a>
                                    ) : (
                                      <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center text-gray-300 shrink-0">
                                        <ExternalLink size={16} />
                                      </div>
                                    )}
                                    <div>
                                      {item.media_permalink ? (
                                        <a
                                          href={item.media_permalink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                                        >
                                          {item.media_product_type === "REELS"
                                            ? "View Reel"
                                            : "View Post"}
                                          <ExternalLink size={12} />
                                        </a>
                                      ) : (
                                        <div className="text-sm text-gray-700">
                                          {item.media_product_type || "-"}
                                        </div>
                                      )}
                                      <div className="text-xs text-gray-400 truncate max-w-[8rem]">
                                        {item.media_id || "-"}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td
                                  className={classNames(
                                    "px-4 py-2 max-w-xs truncate",
                                  )}
                                  title={item.text}
                                >
                                  {isDeleted
                                    ? "Comment was deleted"
                                    : item.text || "-"}
                                  {!isDeleted && item.hidden && (
                                    <span className="ml-2 inline-block px-1.5 py-0.5 text-xs rounded bg-gray-200 text-gray-600 align-middle">
                                      Hidden
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {item.commented_at
                                    ? new Date(
                                        item.commented_at,
                                      ).toLocaleString()
                                    : "-"}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="flex items-center gap-3">
                                    {!isDeleted && !isReply && (
                                      <button
                                        onClick={() => {
                                          setReplyFor(
                                            replyFor === id ? null : id,
                                          );
                                          setReplyText("");
                                        }}
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                      >
                                        <CornerDownRight size={16} />
                                        Reply
                                      </button>
                                    )}
                                    {!isDeleted && canHide && (
                                      <button
                                        onClick={() => handleToggleHidden(item)}
                                        disabled={hidingCommentId === id}
                                        className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 disabled:opacity-50"
                                      >
                                        {item.hidden ? (
                                          <>
                                            <Eye size={16} />
                                            {hidingCommentId === id
                                              ? "..."
                                              : "Unhide"}
                                          </>
                                        ) : (
                                          <>
                                            <EyeOff size={16} />
                                            {hidingCommentId === id
                                              ? "..."
                                              : "Hide"}
                                          </>
                                        )}
                                      </button>
                                    )}
                                    {isDeleted ? (
                                      <span className="text-gray-400 text-sm">
                                        Deleted
                                      </span>
                                    ) : !canDelete ? (
                                      <span
                                        className="text-gray-400 text-sm"
                                        title="Instagram doesn't allow deleting comments on posts you don't own"
                                      >
                                        Not deletable
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => handleDeleteComment(item)}
                                        disabled={deletingCommentId === id}
                                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                                      >
                                        <Trash2 size={16} />
                                        {deletingCommentId === id
                                          ? "Deleting..."
                                          : "Delete"}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          };

                          return topLevel.map((item) => {
                            const id = item._id || item.comment_id;
                            const replies =
                              repliesByParent[item.comment_id] || [];
                            return (
                              <React.Fragment key={id}>
                                {renderRow(item, false)}
                                {replies.map((r) => renderRow(r, true))}
                                {replyFor === id && (
                                  <tr className="border-t bg-gray-50">
                                    <td className="px-4 py-2" colSpan={5}>
                                      <div className="flex items-center gap-2 pl-6">
                                        <input
                                          type="text"
                                          autoFocus
                                          value={replyText}
                                          onChange={(e) =>
                                            setReplyText(e.target.value)
                                          }
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter")
                                              handleReplyToComment(item);
                                            if (e.key === "Escape")
                                              setReplyFor(null);
                                          }}
                                          placeholder="Write a reply..."
                                          className="flex-1 border rounded px-3 py-1 text-sm"
                                        />
                                        <button
                                          onClick={() =>
                                            handleReplyToComment(item)
                                          }
                                          disabled={
                                            postingReply || !replyText.trim()
                                          }
                                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                                        >
                                          {postingReply ? "Posting..." : "Reply"}
                                        </button>
                                        <button
                                          onClick={() => setReplyFor(null)}
                                          className="px-2 py-1 text-sm text-gray-600 hover:text-gray-800"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                    {comments.length === 0 && (
                      <div className="p-4 text-gray-500">
                        No comments found.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-lg text-gray-700 flex flex-col items-center">
          <p>No user data available.</p>
          <p className="mt-4 text-gray-700">
            Connect your Instagram account to use this dashboard.{" "}
            <a
              href="/instagram-connect"
              className="text-blue-600 underline hover:text-blue-800"
            >
              Click here
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
};

export default InstagramBusinessDashboard;
